import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual, xor } from 'lodash';
import TimezoneSelect from 'react-timezone-select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    Slider,
    Switch,
    Typography,
    MenuItem,
    InputLabel,
    Select,
    Card,
    CardContent,
    Grid2 as Grid,
    Stack,
    Autocomplete,
    Chip,
    TextField,
    Tooltip,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Check, Close } from '@mui/icons-material';

import BasePaper from '@/components/ui/BasePaper';
import BaseTextField from '@/components/form/BaseTextField';
import BaseButton from '@/components/ui/BaseButton';

import { StoreInfo, StoreSettings } from '@/types/pages/stores';
import { AURA_PRIMARY_FIELD_OPTION, FieldOption } from '@/types/pages/storeManagement/settings';
import RemoteFirmwareUpdate from './RemoteFirmwareAccordion';
import { useTranslation } from 'react-i18next';
import OfficeAutomationAccordion from './OfficeAutomationAccordion';
import { useLocation } from 'react-router-dom';
import localforage from 'localforage';
import AssetListAccordion from './AssetListAccordion';
import BaseFontIcon from '@/components/ui/BaseFontIcon';
import { useAppContext } from '@/provider/contexts';
import { sendBroadcastCommandAPI, sendUnicastCommandAPI } from '@/api/storeManagement/settings';
import { fetchDeviceList } from '@/api/storeManagement/device';
import { isValidEmail } from '@/utils/emailUtils';

type SystemCommandTab = 'broadcast' | 'unicast';

interface StoreSettingsFormProps {
    storeInfo?: Partial<StoreInfo>;
    assetList?: string[];
    settings: Partial<
        StoreSettings & {
            pp2w_trigger_fields: number[];
        }
    >;
    originalSettings: Partial<StoreSettings>;
    customFieldOptions: FieldOption[];
    validatedResultList: { parameter: string; reason: string }[];
    setSettings: (
        payload: Partial<
            StoreSettings & {
                pp2w_trigger_fields: number[];
            }
        >,
    ) => void;
    onSubmit: () => void;
    onCancel?: () => void;
    isLoading?: boolean;
    hideCancelBtn?: boolean;
    isCorporation?: boolean;
    priorityOptions?: string[];
}
const StoreSettingsForm = ({
    storeInfo = {},
    assetList = [],
    settings,
    originalSettings,
    customFieldOptions,
    validatedResultList,
    setSettings,
    onSubmit,
    onCancel,
    isLoading,
    hideCancelBtn,
    isCorporation = false,
    priorityOptions = [],
}: StoreSettingsFormProps) => {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const location = useLocation();
    const [expandedSections, setExpandedSections] = useState({
        system: true,
        esl: true,
        inventory: false,
        layout: false,
    });
    const layoutSectionRef = useRef<HTMLDivElement | null>(null);
    const [rfuDisable, setRfuDisable] = useState(false);
    const [broadcastCommand, setBroadcastCommand] = useState('');
    const [unicastTargetMac, setUnicastTargetMac] = useState('');
    const [unicastCommand, setUnicastCommand] = useState('');
    const [unicastTargetMacError, setUnicastTargetMacError] = useState('');
    const [isUnicastTargetMacTouched, setIsUnicastTargetMacTouched] = useState(false);
    const [isBroadcastSending, setIsBroadcastSending] = useState(false);
    const [isUnicastSending, setIsUnicastSending] = useState(false);
    const [systemCommandTab, setSystemCommandTab] = useState<SystemCommandTab>('broadcast');
    const [isSystemCommandExpanded, setIsSystemCommandExpanded] = useState(false);

    const allFieldNumList = useMemo(() => {
        return [...AURA_PRIMARY_FIELD_OPTION.map((o) => o.fieldnum), ...customFieldOptions.map((o) => o.fieldnum)];
    }, [customFieldOptions]);

    const OfficeAutoDisable = useMemo(
        () => settings.ohauto_enabled === '1' && (!settings.ohauto_start_times || !settings.ohauto_end_times),
        [settings.ohauto_enabled, settings.ohauto_start_times, settings.ohauto_end_times],
    );

    const [lowBatteryProtectData, setLowBatteryProtectData] = useState({
        isEnabled: Number(settings.prevent_tag_push_threshold) > 0,
        threshold: Number(settings.prevent_tag_push_threshold) || 2.5,
    });

    useEffect(() => {
        if (location.state?.from === 'previewBarcode' && isCorporation) {
            setExpandedSections({
                system: false,
                esl: false,
                inventory: false,
                layout: true,
            });

            setTimeout(() => {
                layoutSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
                const input = layoutSectionRef.current?.querySelector('input');
                input?.focus();
            }, 500);
        }
    }, [location.state, isCorporation]);

    useEffect(() => {
        setLowBatteryProtectData({
            isEnabled: Number(settings.prevent_tag_push_threshold) > 0,
            threshold: Number(settings.prevent_tag_push_threshold) || 2.5,
        });
    }, [settings.prevent_tag_push_threshold]);

    const [validatedMessage, setValidatedMessage] = useState<{ [key: string]: string | null }>({});
    const [reportRecipientInput, setReportRecipientInput] = useState('');
    const reportRecipients = useMemo(
        () =>
            (settings.aura_report_recipient ?? '')
                .split(',')
                .filter((recipient) => recipient !== ''),
        [settings.aura_report_recipient],
    );
    const hasInvalidReportRecipient =
        isCorporation &&
        (reportRecipients.some((recipient) => !isValidEmail(recipient)) ||
            (reportRecipientInput !== '' && !isValidEmail(reportRecipientInput)));

    const isButtonsDisabled = useMemo(() => {
        const noSettingsChanged = isEqual(originalSettings, settings) || rfuDisable || OfficeAutoDisable;
        return noSettingsChanged;
    }, [settings, originalSettings, rfuDisable, OfficeAutoDisable]);

    useEffect(() => {
        if (!validatedResultList.length) return;
        const newMessages: { [key: string]: string | null } = {};
        validatedResultList.forEach(({ parameter, reason }) => {
            newMessages[parameter] = reason;
        });
        setValidatedMessage(newMessages);
    }, [validatedResultList]);

    const handleSettingChange = (key: keyof StoreSettings, value: StoreSettings[keyof StoreSettings]) => {
        setSettings({ ...settings, [key]: value });
    };

    const lowBatteryProtectSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            setLowBatteryProtectData({ isEnabled: false, threshold: 0 });
            handleSettingChange('prevent_tag_push_threshold', '0');
        } else {
            const threshold = lowBatteryProtectData.threshold === 0 ? 2.5 : lowBatteryProtectData.threshold;
            setLowBatteryProtectData({ isEnabled: true, threshold });
            handleSettingChange('prevent_tag_push_threshold', String(threshold));
        }
    };

    const onLowBatteryThresholdChange = (val: string) => {
        setLowBatteryProtectData((prev) => ({ ...prev, threshold: Number(val) }));
        handleSettingChange('prevent_tag_push_threshold', val);
    };

    const [daemonBroadcastDays, setDaemonBroadcastDays] = useState<string[]>([]);
    const dayLabels: Record<string, string> = {
        '0': t('Sun'),
        '1': t('Mon'),
        '2': t('Tue'),
        '3': t('Wed'),
        '4': t('Thu'),
        '5': t('Fri'),
        '6': t('Sat'),
    };

    useEffect(() => {
        if (settings.daemon_broadcast_day) {
            const splitted = settings.daemon_broadcast_day.split(',').filter(Boolean);
            setDaemonBroadcastDays(splitted);
        } else {
            setDaemonBroadcastDays([]);
        }
    }, [settings.daemon_broadcast_day]);

    const handleDaemonDayToggle = (day: string) => {
        let newDays: string[];
        if (daemonBroadcastDays.includes(day)) {
            newDays = daemonBroadcastDays.filter((d) => d !== day);
        } else {
            newDays = [...daemonBroadcastDays, day];
        }
        newDays.sort((a, b) => Number(a) - Number(b));
        const newVal = newDays.join(',');
        setDaemonBroadcastDays(newDays);
        setSettings({ ...settings, daemon_broadcast_day: newVal });
    };
    const [daemonHour, setDaemonHour] = useState('10');
    const [daemonMinute, setDaemonMinute] = useState('00');

    useEffect(() => {
        if (settings.daemon_broadcast_hour) {
            const [h, m] = settings.daemon_broadcast_hour.split(':');
            setDaemonHour(h || '00');
            setDaemonMinute(m || '00');
        } else {
            setDaemonHour('10');
            setDaemonMinute('00');
        }
    }, [settings.daemon_broadcast_hour]);

    const handleDaemonHourChange = (event: SelectChangeEvent<string>) => {
        const newHour = event.target.value as string;
        setDaemonHour(newHour);
        const newVal = `${newHour}:${daemonMinute}`;
        setSettings({ ...settings, daemon_broadcast_hour: newVal });
    };

    const handleDaemonMinuteChange = (event: SelectChangeEvent<string>) => {
        const newMinute = event.target.value as string;
        setDaemonMinute(newMinute);
        const newVal = `${daemonHour}:${newMinute}`;
        setSettings({ ...settings, daemon_broadcast_hour: newVal });
    };

    function handleFieldSelectChange(fieldNum: number): void {
        setSettings({
            ...settings,
            pp2w_trigger_fields: xor(settings.pp2w_trigger_fields, [fieldNum]).sort(),
        });
    }

    function sanitizeDigits(value: string, maxLength = 5): string {
        let cleaned = value.replace(/\D/g, '');

        if (value.length > 1 && cleaned.startsWith('0')) {
            cleaned = cleaned.replace(/^0+/, '');
        }

        return cleaned.slice(0, maxLength);
    }

    function normalizeMac(mac: string): string {
        return mac.trim().toUpperCase();
    }

    function getUnicastTargetMacError(mac: string): string {
        const normalized = normalizeMac(mac);
        if (!normalized) return t('Please fill target MAC address.');
        if (normalized.length !== 12) return t('MAC must be 12 characters');
        if (!/^[0-9A-F]*$/.test(normalized)) return t('Invalid ESL MAC');
        return '';
    }

    const parseCommands = (value: string) =>
        value
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);

    const getCommandValidationError = (commands: string): string => {
        const parsedCommands = parseCommands(commands);
        if (!parsedCommands.length) return '';

        const nonAsciiLine = parsedCommands.find((line) => !/^[\x20-\x7E]+$/.test(line));
        if (nonAsciiLine) {
            return t('Command must use ASCII characters only.');
        }

        const overLengthLine = parsedCommands.find((line) => line.length > 40);
        if (overLengthLine) {
            return t('Each command line must be at most 40 characters.');
        }

        return '';
    };

    const notifyCommandRequired = () =>
        setNotification({
            message: t('Please type the command before sending it.'),
            severity: 'warning',
        });

    const getApiErrorMessage = (message?: string, fallback?: string) => {
        if (message && message.trim().length > 0) return message;
        return fallback || '';
    };

    const resetBroadcastFields = () => {
        setBroadcastCommand('');
    };

    const resetUnicastFields = () => {
        setUnicastTargetMac('');
        setUnicastCommand('');
        setUnicastTargetMacError('');
        setIsUnicastTargetMacTouched(false);
    };

    const handleSystemCommandTabChange = (_event: SyntheticEvent, newValue: SystemCommandTab) => {
        if (isBroadcastSending || isUnicastSending || newValue === systemCommandTab) return;

        if (systemCommandTab === 'broadcast') {
            resetBroadcastFields();
        } else {
            resetUnicastFields();
        }

        setSystemCommandTab(newValue);
    };

    const fetchRtMacAddresses = async (): Promise<string[]> => {
        const storeId = Number(storeInfo.id);
        if (!Number.isFinite(storeId) || storeId <= 0) {
            setNotification({
                message: t('No store selected'),
                severity: 'error',
            });
            return [];
        }

        const corporationId = Number(await localforage.getItem('corporation_id'));
        if (!Number.isFinite(corporationId) || corporationId <= 0) {
            setNotification({
                message: t('No corporation selected'),
                severity: 'error',
            });
            return [];
        }

        const perPage = 500;
        const allRtMacs = new Set<string>();
        let currentPage = 1;
        let maxPage = 1;

        do {
            const {
                data: { data, isSuccess, message },
            } = await fetchDeviceList(corporationId, {
                f_store: storeId,
                f_device_type: '1',
                data_per_page: perPage,
                current_page: currentPage,
            });

            if (!isSuccess) {
                setNotification({
                    message: getApiErrorMessage(message, t('Failed to load settings')),
                    severity: 'error',
                });
                return [];
            }

            data.aaData
                .filter((device) => device.device_type_id === 1)
                .map((device) => device.mac?.trim())
                .filter((mac): mac is string => Boolean(mac))
                .forEach((mac) => allRtMacs.add(mac));

            maxPage = Number(data.max_page) || 1;
            currentPage += 1;
        } while (currentPage <= maxPage);

        return Array.from(allRtMacs);
    };

    const handleSendBroadcast = async () => {
        const commands = parseCommands(broadcastCommand);
        if (!commands.length) {
            notifyCommandRequired();
            return;
        }
        if (commands.length > 5) {
            setNotification({
                message: t('Maximum 5 command lines allowed.'),
                severity: 'warning',
            });
            return;
        }
        const commandValidationError = getCommandValidationError(broadcastCommand);
        if (commandValidationError) {
            setNotification({
                message: commandValidationError,
                severity: 'warning',
            });
            return;
        }
        try {
            setIsBroadcastSending(true);
            const rtMacAddresses = await fetchRtMacAddresses();
            if (!rtMacAddresses.length) {
                setNotification({
                    message: t('No allowed devices found in this store.'),
                    severity: 'warning',
                });
                return;
            }

            for (const commandLine of commands) {
                const { data } = await sendBroadcastCommandAPI({
                    job_type: 'task',
                    payload: commandLine,
                    devices: rtMacAddresses,
                });
                if (!data.isSuccess) {
                    setNotification({
                        message: getApiErrorMessage(data.message, t('Failed to send broadcast command')),
                        severity: 'error',
                    });
                    return;
                }
            }
            setNotification({ message: t('Broadcast command sent'), severity: 'success' });
            resetBroadcastFields();
        } catch {
            setNotification({
                message: t('Failed to send broadcast command'),
                severity: 'error',
            });
        } finally {
            setIsBroadcastSending(false);
        }
    };

    const handleSendUnicast = async () => {
        const targetMac = normalizeMac(unicastTargetMac);
        const commands = parseCommands(unicastCommand);

        setIsUnicastTargetMacTouched(true);
        const macError = getUnicastTargetMacError(targetMac);
        setUnicastTargetMacError(macError);
        if (macError) {
            return;
        }
        if (!commands.length) {
            notifyCommandRequired();
            return;
        }
        if (commands.length > 5) {
            setNotification({
                message: t('Maximum 5 command lines allowed.'),
                severity: 'warning',
            });
            return;
        }
        const commandValidationError = getCommandValidationError(unicastCommand);
        if (commandValidationError) {
            setNotification({
                message: commandValidationError,
                severity: 'warning',
            });
            return;
        }
        try {
            setIsUnicastSending(true);
            for (const commandLine of commands) {
                const { data } = await sendUnicastCommandAPI({
                    job_type: 'task',
                    payload: commandLine,
                    devices: [targetMac],
                });
                if (!data.isSuccess) {
                    setNotification({
                        message: getApiErrorMessage(data.message, t('Failed to send unicast command')),
                        severity: 'error',
                    });
                    return;
                }
            }
            setNotification({ message: t('Unicast command sent'), severity: 'success' });
            resetUnicastFields();
        } catch {
            setNotification({
                message: t('Failed to send unicast command'),
                severity: 'error',
            });
        } finally {
            setIsUnicastSending(false);
        }
    };

    return (
        <BasePaper isFullHeight={false} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* === SYSTEM SETTINGS === */}
                <Accordion
                    expanded={expandedSections.system}
                    onChange={(_, expanded) => setExpandedSections((prev) => ({ ...prev, system: expanded }))}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{t('System Settings')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box>
                                <Typography variant="subtitle1">{t('Store Timezone')}</Typography>
                                <TimezoneSelect
                                    // If store_timezone is null/empty, default to "Etc/GMT"
                                    value={settings.store_timezone || 'Etc/GMT'}
                                    onChange={(newTz) => handleSettingChange('store_timezone', newTz.value)}
                                    labelStyle="original" // 'original' gives e.g. (GMT-10:00) Hawaii
                                />
                                {validatedMessage['store_timezone'] && (
                                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                        {validatedMessage['store_timezone']}
                                    </Typography>
                                )}
                            </Box>
                            {!isCorporation && (
                                <Accordion
                                    expanded={isSystemCommandExpanded}
                                    onChange={(_event, expanded) => setIsSystemCommandExpanded(expanded)}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">{t('System Command')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                spacing={1}
                                                justifyContent="flex-start"
                                                sx={{
                                                    flexWrap: 'wrap',
                                                    rowGap: 1.5,
                                                }}
                                            >
                                                <Tabs
                                                    value={systemCommandTab}
                                                    onChange={handleSystemCommandTabChange}
                                                    variant="scrollable"
                                                    allowScrollButtonsMobile
                                                    sx={{
                                                        '& .MuiTab-root': { textTransform: 'none', minHeight: 0 },
                                                    }}
                                                >
                                                    <Tab value="broadcast" label={t('Broadcast Command')} />
                                                    <Tab value="unicast" label={t('Unicast Command')} />
                                                </Tabs>
                                            </Stack>

                                            {systemCommandTab === 'broadcast' ? (
                                                <Box display="flex" flexDirection="column" gap={1.5}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mt: { sm: 0 }, whiteSpace: 'nowrap' }}
                                                    >
                                                        {t('Custom broadcast command:')}
                                                    </Typography>
                                                    <TextField
                                                        placeholder={t('Type command here')}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                minHeight: 104,
                                                                alignItems: 'flex-start',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                alignSelf: 'flex-start',
                                                                paddingTop: 1.75,
                                                            },
                                                        }}
                                                        value={broadcastCommand}
                                                        onChange={(e) => setBroadcastCommand(e.target.value)}
                                                        disabled={isBroadcastSending}
                                                    />
                                                    <BaseButton
                                                        variant="contained"
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                        }}
                                                        disabled={isBroadcastSending}
                                                        onClick={handleSendBroadcast}
                                                    >
                                                        {isBroadcastSending ? t('Sending...') : t('Send command')}
                                                    </BaseButton>
                                                </Box>
                                            ) : (
                                                <Box display="flex" flexDirection="column" gap={2}>
                                                    <Box display="flex" flexDirection="column" gap={1}>
                                                        <Typography variant="body2">
                                                            {t('Target MAC address:')}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder={t('Type target MAC address here')}
                                                            required
                                                            value={unicastTargetMac}
                                                            onChange={(e) => {
                                                                const next = e.target.value.toUpperCase();
                                                                setUnicastTargetMac(next);
                                                                if (isUnicastTargetMacTouched || next.length > 0) {
                                                                    setUnicastTargetMacError(
                                                                        getUnicastTargetMacError(next),
                                                                    );
                                                                } else {
                                                                    setUnicastTargetMacError('');
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setIsUnicastTargetMacTouched(true);
                                                                setUnicastTargetMacError(
                                                                    getUnicastTargetMacError(unicastTargetMac),
                                                                );
                                                            }}
                                                            error={Boolean(unicastTargetMacError)}
                                                            helperText={unicastTargetMacError}
                                                            disabled={isUnicastSending}
                                                        />
                                                    </Box>
                                                    <Box display="flex" flexDirection="column" gap={1}>
                                                        <Typography variant="body2">
                                                            {t('Custom unicast command:')}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder={t('Type command here')}
                                                            required
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    minHeight: 104,
                                                                    alignItems: 'flex-start',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    alignSelf: 'flex-start',
                                                                    paddingTop: 1.75,
                                                                },
                                                            }}
                                                            value={unicastCommand}
                                                            onChange={(e) => setUnicastCommand(e.target.value)}
                                                            disabled={isUnicastSending}
                                                        />
                                                    </Box>
                                                    <BaseButton
                                                        variant="contained"
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                        }}
                                                        disabled={isUnicastSending}
                                                        onClick={handleSendUnicast}
                                                    >
                                                        {isUnicastSending ? t('Sending...') : t('Send command')}
                                                    </BaseButton>
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            <OfficeAutomationAccordion settings={settings} handleSettingChange={handleSettingChange} />
                            {isCorporation && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">{t('Daily Report Options')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <Typography variant="body2">{t('Report Recipient')}</Typography>
                                            <Autocomplete
                                                multiple
                                                freeSolo
                                                options={[]}
                                                value={reportRecipients}
                                                inputValue={reportRecipientInput}
                                                onInputChange={(_, newInputValue) =>
                                                    setReportRecipientInput(newInputValue)
                                                }
                                                onChange={(_, newValue) => {
                                                    setSettings({
                                                        ...settings,
                                                        aura_report_recipient: newValue
                                                            .filter((o) => o != '')
                                                            .join(','),
                                                    });
                                                }}
                                                renderTags={(value: readonly string[], getTagProps) =>
                                                    value.map((option: string, index: number) => {
                                                        const { key, ...tagProps } = getTagProps({ index });
                                                        return (
                                                            <Chip
                                                                key={key}
                                                                variant="outlined"
                                                                color={isValidEmail(option) ? 'primary' : 'error'}
                                                                label={option}
                                                                {...tagProps}
                                                            />
                                                        );
                                                    })
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        label={t('Emails')}
                                                        placeholder={t('Press Enter to add')}
                                                        error={hasInvalidReportRecipient}
                                                        helperText={
                                                            hasInvalidReportRecipient
                                                                ? t('Please enter a valid email address.')
                                                                : undefined
                                                        }
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            {isCorporation && (
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">{t('Franchise Options')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={settings.hq_product_overwrite === '1'}
                                                        onChange={(e) =>
                                                            handleSettingChange(
                                                                'hq_product_overwrite',
                                                                e.target.checked ? '1' : '0',
                                                            )
                                                        }
                                                    />
                                                }
                                                label={t('Overwrite HQ Products')}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={settings.asset_list_feature === '1'}
                                                        onChange={(e) =>
                                                            handleSettingChange(
                                                                'asset_list_feature',
                                                                e.target.checked ? '1' : '0',
                                                            )
                                                        }
                                                    />
                                                }
                                                label={t('Enable Asset List Feature')}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={settings.register_tags_auto_confirm === '1'}
                                                        onChange={(e) =>
                                                            handleSettingChange(
                                                                'register_tags_auto_confirm',
                                                                e.target.checked ? '1' : '0',
                                                            )
                                                        }
                                                    />
                                                }
                                                label={t('Skip send confirmation to tag')}
                                            />
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            <RemoteFirmwareUpdate
                                settings={settings}
                                originalSettings={originalSettings}
                                setSettings={setSettings}
                                setRfuDisable={setRfuDisable}
                            />
                            {!isCorporation && (
                                <AssetListAccordion
                                    storeInfo={storeInfo as StoreInfo}
                                    assetList={assetList as string[]}
                                />
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* === ESL SETTINGS === */}
                <Accordion
                    expanded={expandedSections.esl}
                    onChange={(_, expanded) => setExpandedSections((prev) => ({ ...prev, esl: expanded }))}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{t('ESL Settings')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container alignItems={'center'} spacing={2} sx={{ my: 1 }}>
                            <Grid size={{ xs: 12, md: 2 }}>
                                <Typography variant="subtitle1">{t('Max auto retry for one job')}</Typography>
                            </Grid>

                            <Grid size={{ xs: 12, md: 10 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <BaseTextField
                                        size="small"
                                        value={settings.max_auto_retry_time || '0'}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                                            let num = Number(raw);
                                            if (num < 0) num = 0;
                                            if (num > 288) num = 288;
                                            handleSettingChange('max_auto_retry_time', String(num));
                                        }}
                                        sx={{
                                            width: { xs: '100%', sm: 150 },
                                        }}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    maxLength: 3,
                                                    pattern: '[0-9]*',
                                                },
                                            },
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            ml: { xs: 0, sm: 1 },
                                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                        }}
                                    >
                                        {t('Maximum value allowed: 288. To disable auto-retry, fill 0 as this value')}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ my: 1 }}>
                            <Grid size={{ xs: 12, md: 2 }}>
                                <Typography variant="subtitle1">{t('Run auto-retry every')}</Typography>
                            </Grid>

                            <Grid size={{ xs: 12, md: 10 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Slider
                                        value={settings.auto_retry_gap_time ? Number(settings.auto_retry_gap_time) : 60}
                                        min={5}
                                        max={60}
                                        step={5}
                                        onChange={(_, val) => handleSettingChange('auto_retry_gap_time', String(val))}
                                        sx={{
                                            width: { xs: '100%', sm: 150 },
                                        }}
                                    />

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            ml: { xs: 0, sm: 1 },
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {`${settings.auto_retry_gap_time || 60} minutes`}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.enable_auto_wake_up === '1'}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const payload: Record<string, string> = {
                                                enable_auto_wake_up: checked ? '1' : '0',
                                            };
                                            if (!checked) {
                                                payload['daily_auto_wk_hour'] =
                                                    originalSettings.daily_auto_wk_hour as string;
                                            }

                                            setSettings({ ...settings, ...payload });
                                        }}
                                    />
                                }
                                label={t('Enable daily auto wake-up')}
                            />
                            {settings.enable_auto_wake_up === '1' && (
                                <>
                                    <Typography>{t('Daily auto wake-up time (0–23)')}</Typography>
                                    <Slider
                                        value={
                                            settings.daily_auto_wk_hour != null
                                                ? Number(settings.daily_auto_wk_hour)
                                                : 9
                                        }
                                        min={0}
                                        max={23}
                                        step={1}
                                        onChange={(_, val) => handleSettingChange('daily_auto_wk_hour', String(val))}
                                        valueLabelDisplay="auto"
                                    />
                                </>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.lowBatteryLayout === '1'}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const payload: Record<string, string> = {
                                                lowBatteryLayout: checked ? '1' : '0',
                                            };
                                            if (!checked) {
                                                payload['low_bat_layout_threshold'] =
                                                    originalSettings.low_bat_layout_threshold as string;
                                            }

                                            setSettings({ ...settings, ...payload });
                                        }}
                                    />
                                }
                                label={t('Enable low battery layout')}
                            />
                            {settings.lowBatteryLayout === '1' && (
                                <>
                                    <Typography>{t('Low Battery Layout Threshold')}</Typography>
                                    <Slider
                                        value={
                                            settings.low_bat_layout_threshold != null
                                                ? Number(settings.low_bat_layout_threshold)
                                                : 2.5
                                        }
                                        min={0}
                                        max={4.2}
                                        step={0.1}
                                        onChange={(_, val) =>
                                            handleSettingChange('low_bat_layout_threshold', String(val))
                                        }
                                        valueLabelDisplay="auto"
                                    />
                                </>
                            )}

                            {/* Low Battery Protect Mode */}
                            <Box>
                                <FormGroup row sx={{ gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={lowBatteryProtectData.isEnabled}
                                                onChange={lowBatteryProtectSwitchChange}
                                            />
                                        }
                                        label={t('Low Battery Protect Mode')}
                                    />
                                    {lowBatteryProtectData.isEnabled && (
                                        <FormControl sx={{ minWidth: 200 }}>
                                            <BaseTextField
                                                label={t('Low Battery Threshold')}
                                                type="number"
                                                size="small"
                                                value={lowBatteryProtectData.threshold}
                                                error={!!validatedMessage['prevent_tag_push_threshold']}
                                                helperText={validatedMessage['prevent_tag_push_threshold']}
                                                onChange={(e) => onLowBatteryThresholdChange(e.target.value)}
                                                slotProps={{
                                                    input: {
                                                        inputProps: {
                                                            step: 0.01,
                                                            min: 0,
                                                            max: 4.2,
                                                        },
                                                        endAdornment: (
                                                            <InputAdornment position="end">{t('volts')}</InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                </FormGroup>
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={2} mt={2}>
                            {/* DAYS CHECKBOXES */}
                            <Typography>{t('Broadcast Days')}</Typography>
                            <FormGroup row>
                                {Object.entries(dayLabels).map(([dayIndex, dayLabel]) => (
                                    <FormControlLabel
                                        key={dayIndex}
                                        control={
                                            <Checkbox
                                                checked={daemonBroadcastDays.includes(dayIndex)}
                                                onChange={() => handleDaemonDayToggle(dayIndex)}
                                            />
                                        }
                                        label={dayLabel}
                                    />
                                ))}
                            </FormGroup>

                            {/* TIME (HH:MM) */}
                            <Typography>{t('Broadcast Time (HH:MM)')}</Typography>
                            <Box display="flex" gap={2}>
                                <FormControl size="small">
                                    <InputLabel>{t('Hour')}</InputLabel>
                                    <Select value={daemonHour} onChange={handleDaemonHourChange} label="Hour">
                                        {Array.from({ length: 24 }, (_, i) => {
                                            const val = String(i).padStart(2, '0');
                                            return (
                                                <MenuItem key={val} value={val}>
                                                    {val}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                <FormControl size="small">
                                    <InputLabel>{t('Minute')}</InputLabel>
                                    <Select value={daemonMinute} onChange={handleDaemonMinuteChange} label="Minute">
                                        {Array.from({ length: 60 }, (_, i) => {
                                            const val = String(i).padStart(2, '0');
                                            return (
                                                <MenuItem key={val} value={val}>
                                                    {val}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Grid container spacing={2} sx={{ my: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    sx={{ mr: 1 }}
                                    control={
                                        <Checkbox
                                            checked={settings.campaign_feature === '1'}
                                            onChange={(e) => {
                                                handleSettingChange('campaign_feature', e.target.checked ? '1' : '0');
                                            }}
                                        />
                                    }
                                    label={t('Enable Campaign Service')}
                                />
                                {settings.campaign_feature === '1' && (
                                    <Tooltip
                                        title={t('When enabled, ESL can display campaign content for linked products.')}
                                        placement="right"
                                        arrow
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <BaseFontIcon
                                                color="info"
                                                className="fa-regular fa-circle-info"
                                                size={16}
                                            />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* === INVENTORY SETTINGS === */}
                <Accordion
                    expanded={expandedSections.inventory}
                    onChange={(_, expanded) => setExpandedSections((prev) => ({ ...prev, inventory: expanded }))}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{t('Inventory Settings')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {/* <Box sx={{ display: 'none' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={settings.use_logo === '1'}
                                                    onChange={(e) =>
                                                        handleSettingChange('use_logo', e.target.checked ? '1' : '0')
                                                    }
                                                />
                                            }
                                            label="Use Logo"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={settings.validate_bc === '1'}
                                                    onChange={(e) =>
                                                        handleSettingChange('validate_bc', e.target.checked ? '1' : '0')
                                                    }
                                                />
                                            }
                                            label="Validate Barcode"
                                        />

                                        <Typography>Barcode for Layout Preview</Typography>
                                        <BaseTextField
                                            value={settings.barcode_for_layout_preview || ''}
                                            onChange={(e) =>
                                                handleSettingChange('barcode_for_layout_preview', e.target.value)
                                            }
                                            fullWidth
                                            error={!!validatedMessage['barcode_for_layout_preview']}
                                            helperText={validatedMessage['barcode_for_layout_preview']}
                                        />
                                    </Box> */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.validate_bc === '1'}
                                        onChange={(e) =>
                                            handleSettingChange('validate_bc', e.target.checked ? '1' : '0')
                                        }
                                    />
                                }
                                label={t("Validate Product's barcode")}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.addLeadingZero === '1'}
                                        onChange={(e) =>
                                            handleSettingChange('addLeadingZero', e.target.checked ? '1' : '0')
                                        }
                                    />
                                }
                                label={t('Add leading zero of product barcode from handheld association')}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.auto_convert_upce_to_upca === '1'}
                                        onChange={(e) =>
                                            handleSettingChange(
                                                'auto_convert_upce_to_upca',
                                                e.target.checked ? '1' : '0',
                                            )
                                        }
                                    />
                                }
                                label={t('Auto Convert UPC-E to UPC-A')}
                            />
                            {/* Shelf Setting */}
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle1">{t('Automatic Mode Shelf')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 4 }}>
                                    <Box>
                                        {/* Enable Shelf */}
                                        <Grid container spacing={2} sx={{ my: 1 }}>
                                            <Grid size={{ xs: 4 }} sx={{ pl: 4 }}>
                                                <Typography variant="subtitle1">{t('Enable Shelf')}</Typography>
                                            </Grid>
                                            <Grid
                                                container
                                                size={{ xs: 8 }}
                                                spacing={1}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Switch
                                                    checked={settings.planogram_enabled === '1'}
                                                    onChange={(e) =>
                                                        handleSettingChange(
                                                            'planogram_enabled',
                                                            e.target.checked ? '1' : '0',
                                                        )
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        {settings.planogram_enabled === '1' && (
                                            <>
                                                {/* Shelf Index */}
                                                <Grid container spacing={2} sx={{ my: 1 }}>
                                                    <Grid size={{ xs: 4 }} sx={{ pl: 4 }}>
                                                        <Typography variant="subtitle1">{t('Shelf Index')}</Typography>
                                                    </Grid>
                                                    <Grid
                                                        container
                                                        size={{ xs: 8 }}
                                                        spacing={1}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <BaseTextField
                                                            size="small"
                                                            value={settings.planogram_index}
                                                            onChange={(e) => {
                                                                const cleaned = sanitizeDigits(e.target.value);
                                                                handleSettingChange('planogram_index', cleaned);
                                                            }}
                                                            sx={{ width: 200 }}
                                                            slotProps={{
                                                                input: {
                                                                    inputProps: {
                                                                        maxLength: 5,
                                                                    },
                                                                },
                                                            }}
                                                            error={!!validatedMessage['planogram_index']}
                                                            helperText={validatedMessage['planogram_index']}
                                                        />
                                                        <Typography variant="body2">
                                                            {t('Define PP index to auto-fetch shelf')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                {/* Prioritize Type */}
                                                <Grid container spacing={2} sx={{ my: 1 }}>
                                                    <Grid size={{ xs: 4 }} sx={{ pl: 4 }}>
                                                        <Typography variant="subtitle1">
                                                            {t('Prioritize Type')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        container
                                                        size={{ xs: 8 }}
                                                        spacing={1}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {isCorporation ? (
                                                            <BaseTextField
                                                                size="small"
                                                                value={settings.planogram_priority_type}
                                                                onChange={(e) => {
                                                                    handleSettingChange(
                                                                        'planogram_priority_type',
                                                                        e.target.value,
                                                                    );
                                                                }}
                                                                sx={{ width: 200 }}
                                                                error={!!validatedMessage['planogram_priority_type']}
                                                                helperText={validatedMessage['planogram_priority_type']}
                                                            />
                                                        ) : (
                                                            <Select
                                                                size="small"
                                                                value={settings.planogram_priority_type}
                                                                onChange={(e) =>
                                                                    handleSettingChange(
                                                                        'planogram_priority_type',
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                displayEmpty
                                                                sx={{ width: 200 }}
                                                            >
                                                                <MenuItem value="">{t('- Not Defined -')}</MenuItem>
                                                                {priorityOptions.map((o, index) => (
                                                                    <MenuItem value={o} key={index}>
                                                                        {o}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}

                                                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                                            {t('Define shelf type to track')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                {/* Show [No Option] */}
                                                <Grid container spacing={2} sx={{ my: 1 }}>
                                                    <Grid size={{ xs: 4 }} sx={{ pl: 4 }}>
                                                        <Typography variant="subtitle1">
                                                            {t('Show [No Option]')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        container
                                                        size={{ xs: 8 }}
                                                        spacing={1}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <Switch
                                                            checked={settings.planogram_show_nooption === '1'}
                                                            onChange={(e) =>
                                                                handleSettingChange(
                                                                    'planogram_show_nooption',
                                                                    e.target.checked ? '1' : '0',
                                                                )
                                                            }
                                                        />
                                                        <Typography variant="body2" sx={{ whiteSpace: 'wrap' }}>
                                                            {t("AURA Mobile will show the 'No Option' option.")}
                                                            <br />
                                                            <strong>
                                                                {t(
                                                                    'Allow users to associate a product without assigning it to a shelf.',
                                                                )}
                                                            </strong>
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                {/* Auto Select Single Option */}
                                                <Grid container spacing={2} sx={{ my: 1 }}>
                                                    <Grid size={{ xs: 4 }} sx={{ pl: 4 }}>
                                                        <Typography variant="subtitle1">
                                                            {t('Auto Select Single Option')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        container
                                                        size={{ xs: 8 }}
                                                        spacing={1}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <Switch
                                                            checked={settings.planogram_autoselectsingleopt === '1'}
                                                            disabled={settings.planogram_show_nooption === '1'}
                                                            onChange={(e) =>
                                                                handleSettingChange(
                                                                    'planogram_autoselectsingleopt',
                                                                    e.target.checked ? '1' : '0',
                                                                )
                                                            }
                                                        />
                                                        <Typography variant="body2" sx={{ whiteSpace: 'wrap' }}>
                                                            {t(
                                                                'AURA Mobile auto-selects the shelf if only one option is available.',
                                                            )}{' '}
                                                            <br />
                                                            <strong>
                                                                {t(
                                                                    "This option will not work if 'Show No Option' is turned on.",
                                                                )}
                                                            </strong>
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                            {/* Product Update Trigger Fields – 視覺封裝成卡片，與右圖風格靠攏 */}
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 2 }}>
                                    {/* 標題 + 說明（統一字級） */}
                                    <Stack>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {t('Product Update Trigger Fields')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(
                                                'New product information will only be pushed to the tag when the selected fields change.',
                                            )}
                                        </Typography>
                                    </Stack>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={
                                                    (settings.pp2w_trigger_fields?.length || 0) ===
                                                    allFieldNumList.length
                                                }
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setSettings({
                                                        ...settings,
                                                        pp2w_trigger_fields: isChecked ? allFieldNumList : [],
                                                    });
                                                }}
                                            />
                                        }
                                        label={t('Select All')}
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '1rem',
                                                lineHeight: 1.4,
                                            },
                                        }}
                                    />
                                    <Divider sx={{ mb: 1.5 }} />

                                    {/* 內容區：左右分欄（對齊、統一間距） */}
                                    <Grid container sx={{ mt: 0 }}>
                                        {/* 左側 Primary */}
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Stack>
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {t('AURA Primary Fields')}
                                                </Typography>

                                                <FormGroup
                                                    sx={{
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '0.875rem',
                                                            lineHeight: 1.4,
                                                        },
                                                    }}
                                                >
                                                    {AURA_PRIMARY_FIELD_OPTION.map((field) => (
                                                        <FormControlLabel
                                                            key={String(field.fieldnum)}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={settings.pp2w_trigger_fields?.includes(
                                                                        field.fieldnum,
                                                                    )}
                                                                    onChange={() =>
                                                                        handleFieldSelectChange(field.fieldnum)
                                                                    }
                                                                />
                                                            }
                                                            label={t(field.key, {
                                                                defaultValue: field.name ?? field.key,
                                                            })}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Stack>
                                        </Grid>

                                        {/* 右側 Custom（自動換行，與左側對齊） */}
                                        <Grid size={{ xs: 12, md: 8 }}>
                                            <Stack>
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {t('AURA Custom Fields')}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            sm: 'repeat(auto-fit, minmax(180px, 1fr))',
                                                        },
                                                    }}
                                                >
                                                    {customFieldOptions.map((field) => (
                                                        <Box key={String(field.fieldnum)} sx={{ minWidth: 0 }}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={settings.pp2w_trigger_fields?.includes(
                                                                            field.fieldnum,
                                                                        )}
                                                                        onChange={() =>
                                                                            handleFieldSelectChange(field.fieldnum)
                                                                        }
                                                                    />
                                                                }
                                                                label={field.name ?? field.key}
                                                                sx={{
                                                                    m: 0,
                                                                    '& .MuiFormControlLabel-label': {
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1.4,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                            <Box sx={{ display: 'none' }}>
                                <Typography>Thousand Separator</Typography>
                                <BaseTextField
                                    value={settings.thousand_separator || ','}
                                    onChange={(e) => handleSettingChange('thousand_separator', e.target.value)}
                                    error={!!validatedMessage['thousand_separator']}
                                    helperText={validatedMessage['thousand_separator']}
                                />

                                <Typography>Decimal Symbol</Typography>
                                <BaseTextField
                                    value={settings.decimal_symbol || '.'}
                                    onChange={(e) => handleSettingChange('decimal_symbol', e.target.value)}
                                    error={!!validatedMessage['decimal_symbol']}
                                    helperText={validatedMessage['decimal_symbol']}
                                />
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* === LAYOUT SETTINGS === */}
                {isCorporation && (
                    <Accordion
                        ref={layoutSectionRef}
                        expanded={expandedSections.layout}
                        onChange={(_, expanded) => setExpandedSections((prev) => ({ ...prev, layout: expanded }))}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">{t('Layout Settings')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2} alignItems="center" sx={{ my: 1 }}>
                                <Grid size={{ xs: 12, md: 2 }}>
                                    <Typography variant="subtitle1">{t('Layout Preview Barcode')}</Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 10 }}>
                                    <BaseTextField
                                        size="small"
                                        value={settings.barcode_for_layout_preview ?? ''}
                                        onChange={(e) => {
                                            handleSettingChange('barcode_for_layout_preview', e.target.value);
                                        }}
                                        sx={{
                                            width: { xs: '100%', md: 320 },
                                        }}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    maxLength: 30,
                                                    pattern: '[0-9]*',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* === SAVE & CANCEL BUTTONS === */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <BaseButton
                    variant="contained"
                    startIcon={<Check />}
                    disabled={isButtonsDisabled || hasInvalidReportRecipient || isLoading}
                    sx={{
                        backgroundColor: 'var(--confirm-btn-color, #1976d2)',
                        color: '#fff',
                        fontWeight: 'bold',
                    }}
                    onClick={onSubmit}
                >
                    {isLoading ? <CircularProgress size={20} color="inherit" /> : t('Save')}
                </BaseButton>
                {!hideCancelBtn && (
                    <BaseButton
                        variant="outlined"
                        startIcon={<Close />}
                        disabled={isButtonsDisabled || isLoading}
                        sx={{
                            borderColor: 'var(--cancel-btn-color, #9c27b0)',
                            color: 'var(--cancel-btn-color, #9c27b0)',
                            fontWeight: 'bold',
                        }}
                        onClick={onCancel}
                    >
                        {t('Cancel')}
                    </BaseButton>
                )}
            </Box>
        </BasePaper>
    );
};

export default StoreSettingsForm;
